import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { FromPerson } from './FunctionsPage';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DraftNotice, useFormDraft } from '../nav/drafts';
import { leaveGuarded, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { COST_MODEL_LABELS, EMPLOYMENT_LABELS, PEOPLE_KEY, PERSONS_RESOURCE, type CostModel, type Employment, type FunctionSummary, type Person, type PersonFunction } from './types';
import { NumberInput } from '../ui/NumberInput';
import { Notice } from '../ui/Notice';

type Account = { id: string; name: string; email: string };

/** What a new person gets when a field is not sent (the server's defaults). */
const DEFAULTS = { phone: '', email: '', notes: '', employment: 'zewnetrzny' as Employment, costModel: 'indywidualny' as CostModel, userId: null as string | null };

const rateText = (grosze: number | null | undefined) => (grosze != null ? plnToInput(grosze) : '');

/** The form's values as kept for the way back (P13); `chosen` as pairs (a Map is not JSON). */
type KeptPerson = { name: string; phone: string; email: string; employment: Employment; costModel: CostModel; rate: string; userId: string; notes: string; chosen: [string, string][] };
/** "" → null; otherwise grosze, or an Error with the message for the user. */
function rateValue(text: string, label: string): number | null {
  if (text.trim() === '') return null;
  try {
    return parsePln(text);
  } catch (e) {
    throw new Error(`${label}: ${(e as Error).message}`);
  }
}

const sameFunctions = (a: PersonFunction[], b: PersonFunction[]) => {
  const key = (l: PersonFunction[]) => JSON.stringify([...l].sort((x, y) => x.functionId.localeCompare(y.functionId)));
  return key(a) === key(b);
};

/** What the form holds: texts as typed; `functions` maps a chosen function to the rate typed for it ('' = default). */
export type PersonDraft = {
  name: string;
  phone: string;
  email: string;
  employment: Employment;
  costModel: CostModel;
  notes: string;
  /** '' = no account */
  userId: string;
  rate: string;
  functions: Map<string, string>;
};

/**
 * The request body (rule 7a): only visible fields; for a new person the name and what differs from the defaults,
 * for an existing one what changed. The person's rate goes only with the model "stały" and the rates in functions
 * only with "indywidualny": a rate hidden by switching the model is neither checked nor sent. Throws an Error with a
 * message for the user when a sent rate is not an amount.
 */
export function personPayload({
  base,
  draft,
  visible,
  showFunctions,
  seeAccounts,
  functionRateVisible,
  functionName = () => '',
}: {
  base?: Person | undefined;
  draft: PersonDraft;
  visible: (field: string) => boolean;
  showFunctions: boolean;
  seeAccounts: boolean;
  functionRateVisible: boolean;
  functionName?: (id: string) => string;
}): Record<string, unknown> {
  const withRates = functionRateVisible && draft.costModel === 'indywidualny';
  const fields: Record<string, unknown> = {
    name: draft.name,
    phone: draft.phone,
    email: draft.email,
    employment: draft.employment,
    costModel: draft.costModel,
    notes: draft.notes,
    userId: draft.userId === '' ? null : draft.userId,
    ...(draft.costModel === 'staly' ? { rateGrosze: rateValue(draft.rate, 'Stawka') } : {}),
    functions: [...draft.functions].map(([functionId, text]): PersonFunction => ({
      functionId,
      ...(withRates ? { rateGrosze: rateValue(text, `Koszt: ${functionName(functionId)}`) } : {}),
    })),
  };
  const sendable = Object.entries(fields).filter(([k]) => (k === 'functions' ? showFunctions : k === 'userId' ? seeAccounts : visible(k)));
  if (!base) {
    const defaults: Record<string, unknown> = { ...DEFAULTS, rateGrosze: null };
    return Object.fromEntries(sendable.filter(([k, v]) => k === 'name' || (k === 'functions' ? (v as unknown[]).length > 0 : v !== defaults[k])));
  }
  const before = base as Record<string, unknown>;
  const baseFunctions = (base.functions ?? []).map((f) => (withRates ? { functionId: f.functionId, rateGrosze: f.rateGrosze ?? null } : { functionId: f.functionId }));
  return Object.fromEntries(
    sendable.filter(([k, v]) =>
      k === 'functions' ? !sameFunctions(v as PersonFunction[], baseFunctions) : v !== (before[k] ?? (k === 'rateGrosze' || k === 'userId' ? null : '')),
    ),
  );
}

/**
 * Add (no `person`) or edit a person. `visible(field)` says which fields of the person the role sees, `functionRateVisible`
 * whether it sees the rates in functions; hidden fields are neither shown nor sent (rule 7a), and on edit only
 * changed fields are sent.
 */
/** The functions as a list in one order (review M3): ticking one off and on again is no change. */
export const sortedFunctions = (list: [string, string][]): [string, string][] => [...list].sort(([a], [b]) => a.localeCompare(b));

export function PersonForm({
  person,
  functions,
  visible,
  functionRateVisible,
  prefill,
  onClose,
}: {
  person?: Person;
  /** A new person made from an account ("Użytkownicy" → "Dodaj jako osobę"): its name, e-mail and the account. */
  prefill?: { name: string; email: string; userId: string } | undefined;
  /** The function dictionary, or null when the role may not see it (then functions are neither shown nor sent). */
  functions: FunctionSummary[] | null;
  visible: (field: string) => boolean;
  functionRateVisible: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const [base, setBase] = useState(person);
  const canEdit = !person || (me !== undefined && can(me, PERSONS_RESOURCE, 'edit'));
  const show = visible;
  // review I1 ("Kto widzi"): the link with an account opens the person's crew events — only the owner sets it
  const seeAccounts = me !== undefined && me.isOwner && can(me, 'identity.users', 'view') && show('userId');
  const accounts = useQuery({ queryKey: ['admin-users'], enabled: seeAccounts, queryFn: () => api<{ users: Account[] }>('/api/admin/users') });

  // P13: what was typed waits for the way back
  const kept = useFormDraft<KeptPerson>(`people.person:${person?.id ?? (prefill ? `account:${prefill.userId}` : 'new')}`, base?.version ?? 0);
  const start = kept.initial;
  const [name, setName] = useState(start?.name ?? person?.name ?? prefill?.name ?? '');
  const [phone, setPhone] = useState(start?.phone ?? person?.phone ?? '');
  const [email, setEmail] = useState(start?.email ?? person?.email ?? prefill?.email ?? '');
  const [employment, setEmployment] = useState<Employment>(start?.employment ?? person?.employment ?? DEFAULTS.employment);
  const [costModel, setCostModel] = useState<CostModel>(start?.costModel ?? person?.costModel ?? DEFAULTS.costModel);
  const [rate, setRate] = useState(start?.rate ?? rateText(person?.rateGrosze));
  const [userId, setUserId] = useState(start?.userId ?? person?.userId ?? prefill?.userId ?? '');
  const [notes, setNotes] = useState(start?.notes ?? person?.notes ?? '');
  /** functionId → rate typed for it ('' = the function's default). */
  const [chosen, setChosen] = useState(() => new Map(start?.chosen ?? (person?.functions ?? []).map((f) => [f.functionId, rateText(f.rateGrosze)] as [string, string])));
  kept.track({ name, phone, email, employment, costModel, rate, userId, notes, chosen: [...chosen] });
  const restore = (d: KeptPerson) => {
    setName(d.name);
    setPhone(d.phone);
    setEmail(d.email);
    setEmployment(d.employment);
    setCostModel(d.costModel);
    setRate(d.rate);
    setUserId(d.userId);
    setNotes(d.notes);
    setChosen(new Map(d.chosen));
  };
  const [localError, setLocalError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = (p: Person) => {
    setBase(p);
    setName(p.name ?? '');
    setPhone(p.phone ?? '');
    setEmail(p.email ?? '');
    setEmployment(p.employment ?? DEFAULTS.employment);
    setCostModel(p.costModel ?? DEFAULTS.costModel);
    setRate(rateText(p.rateGrosze));
    setUserId(p.userId ?? '');
    setNotes(p.notes ?? '');
    setChosen(new Map((p.functions ?? []).map((f) => [f.functionId, rateText(f.rateGrosze)])));
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      base ? api<Person>(`/api/people/persons/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api<Person>('/api/people/persons', { method: 'POST', body }),
    onSuccess: async (p) => {
      load(p);
      kept.clear();
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;
  const showFunctions = functions !== null && show('functions');
  const fnName = new Map((functions ?? []).map((f) => [f.id, f.name ?? '']));

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const body = payload();
    if (body) save.mutate(body);
  }

  /** What a save sends; null when something is wrong (the form says what). */
  function payload(): Record<string, unknown> | null {
    let body: Record<string, unknown>;
    try {
      body = personPayload({
        base,
        draft: { name, phone, email, employment, costModel, notes, userId, rate, functions: chosen },
        visible: show,
        showFunctions,
        seeAccounts,
        functionRateVisible,
        functionName: (id) => fnName.get(id) ?? '',
      });
    } catch (e) {
      setLocalError((e as Error).message);
      return null;
    }
    setLocalError('');
    return body;
  }

  // owner 2026-10-23 13:34: closing with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const valuesOf = (p: Person | undefined) => ({
    name: p ? (p.name ?? '') : (prefill?.name ?? ''),
    phone: p?.phone ?? '',
    email: p ? (p.email ?? '') : (prefill?.email ?? ''),
    employment: p?.employment ?? DEFAULTS.employment,
    costModel: p?.costModel ?? DEFAULTS.costModel,
    rate: rateText(p?.rateGrosze),
    userId: p ? (p.userId ?? '') : (prefill?.userId ?? ''),
    notes: p?.notes ?? '',
    chosen: sortedFunctions((p?.functions ?? []).map((f) => [f.functionId, rateText(f.rateGrosze)] as [string, string])),
  });
  const formEl = useRef<HTMLFormElement | null>(null);
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !sameValues({ name, phone, email, employment, costModel, rate, userId, notes, chosen: sortedFunctions([...chosen]) }, valuesOf(base)),
    save: async () => {
      const body = payload();
      if (!body) return false;
      await save.mutateAsync(body);
      return true;
    },
    discard: () => kept.clear(),
  });

  const toggle = (id: string, on: boolean) =>
    setChosen((prev) => {
      const next = new Map(prev);
      if (on) next.set(id, '');
      else next.delete(id);
      return next;
    });

  const title = base ? `Osoba: ${base.name ?? ''}` : 'Nowa osoba';
  return (
    <form ref={(el) => ((formEl.current = el), guardRef(el))} className="panel form wide" aria-label={title} data-feature-id="people.edit" onSubmit={submit}>
      <h2>{title}</h2>
      <DraftNotice keeper={kept} onRestore={restore} />
      {conflict && (
        <ConflictNotice
          onReload={() => {
            const current = error.current as Person | undefined;
            void (current ? Promise.resolve(current) : api<Person>(`/api/people/persons/${base!.id}`)).then(load);
            save.reset();
          }}
        />
      )}
      {error && !conflict && (
        <p className="alert" role="alert">
          {error.message}
          {fieldIssues(error).map((i) => (
            <span key={i.path + i.message}> {i.message}</span>
          ))}
        </p>
      )}
      {localError && (
        <p className="alert" role="alert">
          {localError}
        </p>
      )}
      {saved && !error && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano.
        </Notice>
      )}
      <label className="field">
        <span>Imię i nazwisko</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoComplete="off" readOnly={!canEdit} />
      </label>
      <div className="field-row">
        {show('phone') && (
          <label className="field">
            <span>Telefon</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" readOnly={!canEdit} />
          </label>
        )}
        {show('email') && (
          <label className="field">
            <span>E-mail</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" readOnly={!canEdit} />
          </label>
        )}
      </div>
      <div className="field-row">
        {show('employment') && (
          <label className="field">
            <span>Zatrudnienie</span>
            <select value={employment} disabled={!canEdit} onChange={(e) => setEmployment(e.target.value as Employment)}>
              {(Object.keys(EMPLOYMENT_LABELS) as Employment[]).map((k) => (
                <option key={k} value={k}>
                  {EMPLOYMENT_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
        )}
        {show('costModel') && (
          <label className="field">
            <span>Model kosztu</span>
            <select value={costModel} disabled={!canEdit} onChange={(e) => setCostModel(e.target.value as CostModel)}>
              {(Object.keys(COST_MODEL_LABELS) as CostModel[]).map((k) => (
                <option key={k} value={k}>
                  {COST_MODEL_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
        )}
        {show('rateGrosze') && costModel === 'staly' && (
          <label className="field">
            <span>Stawka (zł)</span>
            <NumberInput kind="money" value={rate} onValueChange={setRate} readOnly={!canEdit} placeholder="np. 450" />
          </label>
        )}
      </div>

      {functions !== null && showFunctions && (
        <fieldset className="fn-choices">
          <legend>Funkcje, które może pełnić</legend>
          {functions.length === 0 && (
            <p className="muted">
              Słownik funkcji jest pusty. Dodaj funkcje w zakładce <Link to="/osoby/funkcje">„Funkcje”</Link>.
            </p>
          )}
          {functionRateVisible && costModel === 'indywidualny' && functions.length > 0 && (
            <p className="hint">Koszt osoby w funkcji (ile płacimy za dzień). Puste pole: domyślny koszt funkcji. Wpisanie kosztu zaznacza funkcję; odznaczenie funkcji kasuje jej koszt.</p>
          )}
          {functions.map((f) => {
            const on = chosen.has(f.id);
            // owner 2026-10-25: every function with its cost for this person, also the ones not chosen yet
            const showRate = functionRateVisible && costModel === 'indywidualny';
            return (
              <div key={f.id} className="fn-choice">
                <label className="check">
                  <input type="checkbox" checked={on} disabled={!canEdit} onChange={(e) => toggle(f.id, e.target.checked)} />
                  <span>{f.name}</span>
                </label>
                {on && base && (
                  <Link
                    className="fn-tasks-link"
                    to={`/osoby/funkcje?funkcja=${f.id}`}
                    state={{ fromPerson: { to: `/osoby?osoba=${base.id}`, name: base.name ?? '' } } satisfies FromPerson}
                    aria-label={`Zadania funkcji: ${f.name ?? ''}`}
                  >
                    Zadania ›
                  </Link>
                )}
                {showRate && (
                  <NumberInput
                    className="fn-rate"
                    kind="money"
                    aria-label={`Koszt: ${f.name ?? ''}`}
                    value={chosen.get(f.id) ?? ''}
                    // a cost typed for a function not chosen chooses it (owner 2026-10-25)
                    onValueChange={(v) => setChosen((prev) => (v === '' && !prev.has(f.id) ? prev : new Map(prev).set(f.id, v)))}
                    readOnly={!canEdit}
                    placeholder={f.defaultRateGrosze != null ? `domyślnie ${formatPln(f.defaultRateGrosze)}` : 'koszt (zł)'}
                  />
                )}
              </div>
            );
          })}
        </fieldset>
      )}

      {seeAccounts && (
        <label className="field">
          <span>Konto w aplikacji</span>
          <select value={userId} disabled={!canEdit} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Bez konta</option>
            {(accounts.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
        </label>
      )}
      {!seeAccounts && base?.userId && <p className="muted">Osoba ma konto w aplikacji.</p>}
      {base?.userId && base.accountActive === false && <p className="muted">Konto tej osoby jest nieaktywne — osoba zostaje w Osobach (i w historii obsady).</p>}
      {show('notes') && (
        <label className="field">
          <span>Uwagi</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} readOnly={!canEdit} />
        </label>
      )}
      <div className="actions">
        {canEdit && (
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj osobę'}
          </button>
        )}
        <button
          className="btn"
          type="button"
          onClick={() =>
            leaveGuarded(() => {
              kept.clear();
              onClose();
            }, formEl.current)
          }
        >
          Zamknij
        </button>
      </div>
    </form>
  );
}
